/** 足行天下 API（真实接口） - 作者: zbq, 日期: 2026-05-29 */

import { zqRequest, clearZqSession } from '@/sheep/request/zq';
import { getZqApiBase } from '@/sheep/config/zq';
import {
  ok,
  fail,
  getUserId,
  getDailishangId,
  getShareId,
  getHomeMerchantFilterId,
  getShareInvite,
  getInviteShopFilterId,
  appendPromoFields,
  setZqSession,
  syncTenantFromMember,
  syncLoginState,
  logoutLocal,
  zqCall,
  pickList,
  footballStatusByTab,
  footballTimeRange,
  mapSort,
} from './helpers';
import { getWxLoginApi } from './wx-auth';
import {
  mapMember,
  mapArticle,
  mapArticleDetail,
  mapPastRecord,
  sortPastByPeriodDesc,
  mapFootballMatch,
  mapMerchant,
  mapOrder,
  mapTag,
  mapBalanceDetail,
  mapWithdraw,
  mapRechargeTier,
  mapChild,
  mapForumFromMerchant,
  mapAgreement,
  mapAbout,
  mapDisplayConfig,
  mapPayResult,
} from './mappers';
import { validatePassword } from '@/sheep/helper/password';

let currentUser = null;
let isLoggedIn = syncLoginState();
let displayConfigCache = null;

const GUEST_USER = {
  id: 0,
  nickname: 'momo',
  avatar: '/static/zvkf9s/logo.png',
  mobile: '',
  balance: 0,
  beans: 0,
  commissionRate: 0,
  role: 'guest',
  isExpert: false,
  isAgent: false,
};

async function fetchArticles(params = {}) {
  // 首页：扫推广码 / 商家账号 → 只看该商家方案；其它场景不塞空店代理过滤
  const isIndex = params.isIndex ?? '1';
  let agentId = '';
  if (Object.prototype.hasOwnProperty.call(params, 'id')) {
    agentId = params.id || '';
  } else if (String(isIndex) === '1') {
    agentId = getHomeMerchantFilterId();
  } else {
    agentId = getDailishangId();
  }
  // 首页在售默认拉满（PageParam 上限 200）；避免 3D 等栏目今日超过 50 条被截断
  const defaultSize = String(isIndex) === '1' ? 200 : 50;
  const res = await zqCall('api/getMyArticleList', {
    title: params.keyword || '',
    id: agentId,
    pageNo: params.pageNo || 1,
    pageSize: params.pageSize || defaultSize,
    sort: mapSort(params.sort),
    uid: getUserId(),
    isIndex,
    manage: params.manage || '',
    matchId: params.matchId,
    nickname: params.nickname,
    isBefore: params.isBefore,
  }, { showLoading: false });
  if (res.code !== 0) return res;
  // 往期也不再 forcePaid：正文是否可见只跟随后端 contentOpen/gongkai/已购
  let list = pickList(res.data).map((item) => mapArticle(item)).filter(Boolean);
  // 已购方案仍展示在首页（可多人买）；勿按 pay/isPaid 过滤，作者本人 pay=1 会把自己方案滤光
  // 商家空列表不再回退平台在售：挂靠方案须「一键同步」后才有本店副本
  return ok({ list, total: res.data.totalCount || list.length });
}

const ZqApi = {
  async login({ mobile, password, code, loginType }) {
    const phone = (mobile || '').trim();
    if (!phone) return fail('请输入手机号');

    const payload = { phone };
    if (loginType === 'sms') {
      if (!code) return fail('请输入验证码');
      // HTTPS 传密码，服务端 BCrypt；不再客户端 MD5
      payload.password = password || '';
      payload.code = code;
    } else {
      if (!password) return fail('请输入密码');
      payload.password = password;
      if (code) payload.code = code;
    }
    appendPromoFields(payload);

    try {
      const res = await zqRequest({ url: 'api/login', data: payload });
      const body = res.data;
      setZqSession(body);
      uni.setStorageSync('phone', phone);
      isLoggedIn = true;

      let member = mapMember(body);
      if (body.id && !body.nickname) {
        const profile = await this.getMemberById();
        if (profile.code === 0) member = profile.data;
      }
      if (member) currentUser = member;
      if (member?.isMerchant) {
        uni.setStorageSync('zq_is_merchant', true);
        uni.setStorageSync('zq_role', 'merchant');
      } else if (member) {
        uni.setStorageSync('zq_is_merchant', false);
        uni.setStorageSync('zq_role', 'user');
      }
      if (body.promoterId) {
        uni.setStorageSync('wxfid', String(body.promoterId));
      }

      return {
        code: 0,
        msg: res.msg || '登录成功',
        data: { token: String(body.id || ''), member },
        needVerify: res.needVerify,
      };
    } catch (err) {
      return err;
    }
  },

  async wxLogin(params = {}) {
    const code = params.code;
    if (!code) {
      return fail('授权失败，请重新授权');
    }
    try {
      const res = await zqRequest({
        url: getWxLoginApi(),
        data: appendPromoFields({
          code,
          ...params,
        }),
      });
      const body = res.data;
      setZqSession(body);
      isLoggedIn = true;
      currentUser = mapMember(body);
      if (currentUser?.isMerchant) {
        uni.setStorageSync('zq_is_merchant', true);
        uni.setStorageSync('zq_role', 'merchant');
      } else if (currentUser) {
        uni.setStorageSync('zq_is_merchant', false);
        uni.setStorageSync('zq_role', 'user');
      }
      if (body.promoterId) {
        uni.setStorageSync('wxfid', String(body.promoterId));
      }
      return ok({ token: String(body.id || ''), member: currentUser });
    } catch (err) {
      return fail(err.msg || '微信登录失败，请使用账号登录');
    }
  },

  async userRegist({ mobile, password, code, fid }) {
    const phone = (mobile || '').trim();
    if (!phone || !password) return fail('请填写完整信息');
    if (!code) return fail('请输入验证码');
    const pwdErr = validatePassword(password);
    if (pwdErr) return fail(pwdErr);

    const body = appendPromoFields({
      phone,
      password,
      surePassword: password,
      code,
    });
    const res = await zqCall('api/userRegist', body, { modal: true });
    if (res.code === 0) uni.setStorageSync('phone', phone);
    return res.code === 0 ? ok(null, '注册成功，请去登录吧！') : res;
  },

  async sendSms({ mobile, scene = 'register' }) {
    const phone = (mobile || '').trim();
    if (!phone) return fail('请输入手机号');
    const res = await zqCall('api/sendSms', { phone, scene });
    return res.code === 0 ? ok(null, '验证码已发送') : res;
  },

  async findPassword({ mobile, password, code }) {
    const phone = (mobile || '').trim();
    if (!phone || !password || !code) return fail('请填写完整信息');
    const pwdErr = validatePassword(password);
    if (pwdErr) return fail(pwdErr);
    const res = await zqCall('api/findPassword', {
      phone,
      password,
      surePassword: password,
      code,
    }, { modal: true });
    return res.code === 0 ? ok(null, '密码重置成功，请登录') : res;
  },

  async getMemberById(id) {
    const memberId = id || getUserId();
    if (!memberId) {
      isLoggedIn = false;
      return ok({ ...GUEST_USER });
    }
    const res = await zqCall('api/getMemberById', { id: memberId }, { showLoading: false });
    if (res.code !== 0) {
      isLoggedIn = false;
      // 勿吞掉错误返回访客成功态，否则「我的」会一直显示「成为商家」而不是「我要推广」
      return res;
    }
    const member = mapMember(res.data) || { ...GUEST_USER };
    setZqSession({
      ...res.data,
      id: member.id,
      isMerchant: member.isMerchant,
      merchant: member.isMerchant,
    });
    syncTenantFromMember(res.data);
    currentUser = member;
    isLoggedIn = true;
    return ok(member);
  },

  async modifyMember(data = {}) {
    const id = getUserId();
    if (!id) return fail('请先登录');
    const payload = { id };
    if (data.mobile || data.phone) payload.phone = data.mobile || data.phone;
    if (data.nickname) payload.nickname = data.nickname;
    if (data.avatar || data.icon) payload.icon = data.avatar || data.icon;
    if (data.password) {
      const pwdErr = validatePassword(data.password);
      if (pwdErr) return fail(pwdErr);
      payload.password = data.password;
    }
    const res = await zqCall('api/modifyMember', payload, { modal: true });
    if (res.code === 0) {
      const profile = await this.getMemberById();
      return profile;
    }
    return res;
  },

  async zhuxiao() {
    const id = getUserId();
    if (id) await zqCall('api/zhuxiao', { id }, { showLoading: true });
    isLoggedIn = false;
    currentUser = null;
    logoutLocal();
    return ok(null);
  },

  async getFootballList2(params = {}) {
    const tabStatus = footballStatusByTab(params.tab);
    const range = footballTimeRange(params.tab);
    const res = await zqCall('api/getFootballList2', {
      language: 'zh',
      status: tabStatus,
      name: params.keyword || '',
      page: params.pageNo || 1,
      perPage: params.pageSize || 20,
      ...range,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const rawList = res.data?.data?.list || res.data?.list || [];
    const list = rawList.map((item) => mapFootballMatch(item, tabStatus));
    return ok({ list, total: res.data?.data?.total || res.data?.total || list.length });
  },

  async getFootballDetail(id) {
    const listRes = await this.getFootballList2({ pageSize: 100 });
    let match = null;
    if (listRes.code === 0) {
      match = listRes.data.list.find((m) => String(m.id) === String(id));
    }
    if (!match) {
      match = {
        id,
        leagueName: '',
        homeTeam: '',
        awayTeam: '',
        matchTime: '',
        status: '未开始',
        homeScore: 0,
        awayScore: 0,
      };
    }
    const articlesRes = await fetchArticles({ matchId: id, pageSize: 20, isIndex: '' });
    return ok({ ...match, articles: articlesRes.code === 0 ? articlesRes.data.list : [] });
  },

  async getMerchantList(params = {}) {
    const res = await zqCall('api/getMerchantList', {
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
      nickname: params.keyword || '',
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map(mapMerchant).filter(Boolean);
    return ok({ list, total: list.length });
  },

  async getMerchantDetail(id) {
    const res = await zqCall('api/getMemberById', { id }, { showLoading: false });
    if (res.code !== 0) return fail('专家不存在');
    const merchant = mapMerchant(res.data);
    const articlesRes = await fetchArticles({ pageSize: 50, isIndex: '', nickname: merchant.nickname });
    return ok({ ...merchant, articles: articlesRes.code === 0 ? articlesRes.data.list : [] });
  },

  async guanzhu({ merchantId, action }) {
    const res = await zqCall('api/guanzhu', {
      id: getUserId(),
      uid: merchantId,
      attention: action === 'follow' ? '1' : '0',
    });
    return res.code === 0 ? ok({ success: true }) : res;
  },

  async userApply(data = {}) {
    const res = await zqCall('api/userApply', {
      id: getUserId(),
      phone: data.mobile || data.phone,
      name: data.name || data.realName,
      code: data.code || data.veriyCode,
      ...data,
    }, { modal: true });
    return res.code === 0 ? ok({ msg: '申请已提交，请等待审核', ...data }) : res;
  },

  async getBiaoqianList() {
    const merchantId = getHomeMerchantFilterId();
    const res = await zqCall('api/getBiaoqianList', {
      merchantId: merchantId || undefined,
      wxfid: getShareId() || undefined,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    // 首页只展示已绑定栏目
    const list = pickList(res.data)
      .map(mapTag)
      .filter((t) => t && t.bound !== false && String(t.name || t.title || '').trim());
    return ok(list);
  },

  async getBiaoqianManageList() {
    const res = await zqCall('api/getBiaoqianManageList', {
      id: getUserId(),
      uid: getUserId(),
    });
    if (res.code !== 0) return res;
    return ok(pickList(res.data).map(mapTag));
  },

  async saveBiaoqianSettings(list = []) {
    const res = await zqCall('api/saveBiaoqianSettings', {
      id: getUserId(),
      uid: getUserId(),
      list,
    }, { modal: true });
    return res.code === 0 ? ok(res.data) : res;
  },

  async getPlatformAffiliate() {
    const res = await zqCall('api/getPlatformAffiliate', {
      id: getUserId(),
      uid: getUserId(),
    }, { showLoading: false });
    if (res.code !== 0) return res;
    return ok({
      enabled: !!(res.data?.enabled || res.data?.enabled === '1'),
      tenantId: Number(res.data?.tenantId || 0),
    });
  },

  async setPlatformAffiliate(enabled) {
    const res = await zqCall('api/setPlatformAffiliate', {
      id: getUserId(),
      uid: getUserId(),
      enabled: enabled ? '1' : '0',
    }, { modal: true });
    return res.code === 0 ? ok(res.data) : res;
  },

  async getMerchantAffiliateHome() {
    const res = await zqCall('api/getMerchantAffiliateHome', {
      id: getUserId(),
      uid: getUserId(),
    }, { showLoading: false });
    return res.code === 0 ? ok(res.data) : res;
  },

  async applyMerchantAffiliate(payload = {}) {
    const res = await zqCall('api/applyMerchantAffiliate', {
      id: getUserId(),
      uid: getUserId(),
      ...payload,
    }, { modal: true });
    return res.code === 0 ? ok(res.data, res.msg) : res;
  },

  async listMerchantAffiliate(tab = 'mine', keyword = '') {
    const res = await zqCall('api/listMerchantAffiliate', {
      id: getUserId(),
      uid: getUserId(),
      tab,
      keyword,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    return ok({ list: pickList(res.data), total: res.data?.totalCount || 0 });
  },

  async auditMerchantAffiliate(applyId, pass) {
    const res = await zqCall('api/auditMerchantAffiliate', {
      id: getUserId(),
      uid: getUserId(),
      applyId,
      pass: pass ? '1' : '0',
    }, { modal: true });
    return res.code === 0 ? ok(res.data, res.msg) : res;
  },

  /** 2026-11-05：一级商家可直接开启挂靠的下级商家；未申请过的显示「未挂靠」 */
  async listAffiliateCandidates() {
    const res = await zqCall('api/listAffiliateCandidates', {
      id: getUserId(),
      uid: getUserId(),
    }, { showLoading: false });
    const body = res?.data || {};
    // ZqResult 的业务提示字段是 resultNote（不是 msg），失败时必须把原因带给用户
    const note = body.resultNote || body.msg || '';
    if (res?.code !== 0 || (body.result !== undefined && body.result !== '0' && body.result !== 0)) {
      return fail(note || '加载下级商家失败');
    }
    return ok({ list: pickList(body), total: body.totalCount || 0 });
  },

  /** 一级商家直接开启下级挂靠（无需下级申请），成功后该店状态变为「已挂靠」 */
  async enableChildAffiliate(tenantId) {
    const res = await zqCall('api/enableChildAffiliate', {
      id: getUserId(),
      uid: getUserId(),
      tenantId: String(tenantId || ''),
      childTenantId: String(tenantId || ''),
    }, { modal: true });
    const body = res?.data || {};
    const note = body.resultNote || body.msg || '';
    if (res?.code !== 0 || (body.result !== undefined && body.result !== '0' && body.result !== 0)) {
      return fail(note || '开启挂靠失败');
    }
    return ok(body, note || '已开启挂靠');
  },

  /** 一级代理给名下二级代理设置提成比例（如 85 表示二级拿 85%） */
  async setMerchantAffiliatePercent(applyId, percent) {
    const res = await zqCall('api/setMerchantAffiliatePercent', {
      id: getUserId(),
      uid: getUserId(),
      applyId: String(applyId || ''),
      percent: Number(percent),
    }, { modal: true });
    return res.code === 0 ? ok(res.data, res.msg) : res;
  },

  /**
   * 商家自行解除挂靠（2026-11-03）
   * @param {string} applyId 挂靠记录 id（listMerchantAffiliate('mine') 返回的 id）
   */
  async cancelMerchantAffiliate(applyId) {
    const res = await zqCall('api/cancelMerchantAffiliate', {
      id: getUserId(),
      uid: getUserId(),
      applyId: String(applyId || ''),
    }, { modal: true });
    return res.code === 0 ? ok(res.data, res.msg) : res;
  },

  async getArticleById(id, opts = {}) {
    const aid = String(id || '').trim();
    const res = await zqCall('api/getArticleById', {
      id: aid,
      articleId: aid,
      uid: getUserId(),
      wxfid: getHomeMerchantFilterId() || getShareId() || undefined,
      // 编辑场景（方案管理编辑页回填）才向店主本人回传付费正文；首页/分享详情不放
      forEdit: opts.edit ? '1' : undefined,
    }, { silent: !!opts.silent, showLoading: opts.showLoading !== false });
    if (res.code !== 0) {
      const m = String(res.msg || '');
      return fail(m.includes('不存在') ? '方案不存在或已下架' : (m || '方案不存在或已下架'));
    }
    const raw = res.data?.id ? res.data : (res.data?.data || res.data);
    const article = mapArticleDetail(raw, getUserId());
    if (!article) return fail('方案不存在或已下架');

    const shopFilter =
      getHomeMerchantFilterId() ||
      (raw?.settleUserId && /^t\d+_agent001$/.test(String(raw.settleUserId))
        ? String(raw.settleUserId)
        : '') ||
      '';
    const authorNick = String(raw?.nickname || article.merchantName || '').trim();
    // 往期：必须带本店结算号；有店过滤时才加 nickname（同专家），避免无店过滤时跨挂靠店重复
    const pastRes = await zqCall(
      'api/getMyArticleList',
      {
        id: shopFilter || undefined,
        pageNo: 1,
        pageSize: 50,
        isIndex: '1',
        isBefore: '1',
        excludeId: aid,
        nickname: shopFilter && authorNick ? authorNick : undefined,
        uid: getUserId(),
      },
      { showLoading: false },
    );
    const expertKey = String(raw?.expertId || raw?.userId || raw?.uid || '').trim();
    const pastRaw = (pastRes.code === 0 ? pickList(pastRes.data) : []).filter(
      (item) => String(item.id) !== String(id),
    );
    // 同专家：本店内同一老师的往期
    const sameAuthor = pastRaw.filter((item) => {
      if (!expertKey && !authorNick) return true;
      const eid = String(item.expertId || item.userId || item.uid || '').trim();
      if (expertKey && eid === expertKey) return true;
      const nick = String(item.nickname || item.user?.nickname || '').trim();
      return !!(authorNick && nick === authorNick);
    });
    // 同源去重（sourceArticleId / 自身 id）：只留一条，优先有正文/开奖码/已复盘
    const dedup = new Map();
    const rank = (x) =>
      (String(x.payContent || '').trim() ? 8 : 0) +
      (String(x.drawNumbers || '').trim() ? 4 : 0) +
      (x.hit === '1' || x.hit === 1 ? 3 : 0) +
      (x.hit === '0' || x.hit === 0 ? 2 : 0) +
      (x.hit === '2' || x.hit === 2 ? 1 : 0) +
      (x.gongkai === '1' || x.gongkai === 1 ? 1 : 0);
    for (const item of sameAuthor) {
      const key = String(item.sourceArticleId || item.id);
      const prev = dedup.get(key);
      if (!prev || rank(item) > rank(prev)) dedup.set(key, item);
    }
    // 按期数倒序，最多展示 30 期（覆盖后台常见往期数量）
    article.pastRecords = sortPastByPeriodDesc(Array.from(dedup.values()))
      .slice(0, 30)
      .map(mapPastRecord)
      .filter(Boolean);

    return ok(article);
  },

  async getArticleList(params = {}) {
    return fetchArticles({ ...params, isIndex: '1' });
  },

  async getPastArticleList(params = {}) {
    const res = await fetchArticles({
      ...params,
      isIndex: '1',
      isBefore: '1',
      pageSize: params.pageSize || 50,
    });
    if (res.code !== 0) return res;
    const list = sortPastByPeriodDesc(res.data?.list || []);
    return ok({ ...(res.data || {}), list });
  },

  async getPastRecords(merchantId, nickname) {
    const res = await zqCall(
      'api/getMyArticleList',
      {
        pageNo: 1,
        pageSize: 20,
        isIndex: '',
        isBefore: '1',
        nickname,
        uid: getUserId(),
      },
      { showLoading: false },
    );
    if (res.code !== 0) return res;
    const list = sortPastByPeriodDesc(
      pickList(res.data)
        .filter((item) => !merchantId || String(item.userId || item.user?.id) === String(merchantId))
        .map(mapPastRecord)
        .filter(Boolean),
    );
    return ok(list);
  },

  async getMyArticleList(params = {}) {
    const res = await fetchArticles({
      ...params,
      isIndex: '',
      manage: '1',
      pageSize: params.pageSize || 100,
    });
    return res;
  },

  /** 一键发布：多选专家批量创建方案 */
  async batchSaveArticles(data = {}) {
    const res = await zqCall(
      'api/batchSaveArticles',
      {
        uid: getUserId(),
        id: getUserId(),
        ...data,
      },
      { modal: true },
    );
    return res.code === 0 ? ok(res.data || res) : res;
  },

  async saveArticle1(data = {}) {
    const gongkai = data.gongkai === '1' || data.gongkai === 1 ? '1' : '0';
    const payload = {
      uid: getUserId(),
      id: getUserId(),
      fabu: data.fabu || '1',
      addTime: data.addTime,
      dsTime: data.dsTime || '',
      money: data.price || data.money,
      payContent: data.payContent || data.content,
      payImage: data.payImage || data.contentPics,
      title: data.title,
      nickname: data.nickname || currentUser?.nickname,
      qishu: data.period || data.qishu,
      matchId: data.matchId,
      type: data.type,
      subTitle: data.summary || data.subTitle,
      lableName: data.tagName || data.lableName,
      team1: data.team1 || data.homeTeam || '',
      team2: data.team2 || data.awayTeam || '',
      league: data.league || '',
      ...data,
      // 放在 spread 后，避免被覆盖；保证与后台「公开」同一字段
      gongkai,
      payContent: data.payContent || data.content || '',
      introContent: data.introContent,
      disclaimer: data.disclaimer,
      drawNumbers: data.drawNumbers || '',
      uid: getUserId(),
      id: getUserId(),
    };
    const res = await zqCall('api/saveArticle1', payload, { modal: true });
    return res.code === 0 ? ok(mapArticle(res.data) || res.data) : res;
  },

  /**
   * 本店专家列表（一键发布 / 新增方案选人 / 我的→专家管理 共用）
   * @param {string} [scope] 传 'manage' 时只返回本店自建专家（专家管理页用；不含上级/平台只读档案）
   */
  async getShopExpertList(scope) {
    const payload = { uid: getUserId(), id: getUserId() };
    if (scope) payload.scope = scope;
    const res = await zqCall('api/getShopExpertList', payload);
    if (res.code !== 0) return [];
    return Array.isArray(res.data?.dataList) ? res.data.dataList : [];
  },

  // 2026-10-26：H5「我的 → 专家管理」——商家自建/改本店作者档案
  async saveShopExpert(data = {}) {
    const payload = {
      uid: getUserId(),
      id: getUserId(),
      expertId: data.id || undefined,
      nickname: (data.nickname || '').trim(),
      lableName: (data.lableName || '').trim(),
      articlePrice: data.articlePrice,
      groupCount: data.groupCount,
      playMethods: (data.playMethods || '').trim() || undefined,
      listed: data.listed === false || data.listed === 0 ? 0 : 1,
      // 2026-10-31：专家头像（http(s) URL；空串表示清空）
      icon: data.icon != null ? String(data.icon).trim() : undefined,
    };
    const res = await zqCall('api/saveShopExpert', payload, { modal: true });
    return res;
  },

  /**
   * 2026-10-31：上传专家头像（multipart，zq uid 鉴权，不依赖芋道 JWT）
   * @returns {{ code:number, msg?:string, data?:{ url:string, icon:string } }}
   */
  uploadShopExpertIcon(filePath) {
    return new Promise((resolve) => {
      if (!filePath) {
        resolve({ code: -1, msg: '请选择图片' });
        return;
      }
      const uid = getUserId();
      if (!uid) {
        resolve({ code: -1, msg: '请先登录' });
        return;
      }
      uni.showLoading({ title: '上传中', mask: true });
      uni.uploadFile({
        url: `${getZqApiBase()}api/uploadShopExpertIcon`,
        filePath,
        name: 'file',
        formData: { uid, id: uid },
        header: {
          'tenant-id': String(uni.getStorageSync('tenant-id') || '1'),
          uid: String(uid),
        },
        success: (up) => {
          try {
            const body = typeof up.data === 'string' ? JSON.parse(up.data) : up.data || {};
            // ZqResult 业务字段平铺：url/icon 在根上，不在 data 里
            const result = body.result != null ? String(body.result) : '';
            const nested = body.data && typeof body.data === 'object' ? body.data : {};
            const url = body.url || body.icon || nested.url || nested.icon || '';
            const okBiz = result === '0' || result === '3' || Number(body.code) === 0;
            resolve({
              code: okBiz && url ? 0 : -1,
              msg: body.resultNote || body.msg || '',
              data: { url, icon: url },
            });
          } catch (e) {
            resolve({ code: -1, msg: '上传响应异常' });
          }
        },
        fail: () => resolve({ code: -1, msg: '上传失败，请重试' }),
        complete: () => uni.hideLoading(),
      });
    });
  },

  async deleteShopExpert(expertId) {
    if (!expertId) return { code: -1, msg: '专家不存在' };
    return await zqCall('api/deleteShopExpert', {
      uid: getUserId(),
      id: getUserId(),
      expertId,
    }, { modal: true });
  },

  async getExpertLatestArticle(expertId) {
    if (!expertId) return null;
    const res = await zqCall('api/getExpertLatestArticle', {
      uid: getUserId(),
      id: getUserId(),
      expertId,
    });
    if (res.code !== 0) return null;
    const data = res.data || {};
    return data.articleId ? data : null;
  },

  async updateArticle(data = {}) {
    const articleId = data.articleId || data.id;
    const gongkai =
      data.gongkai === undefined || data.gongkai === null || data.gongkai === ''
        ? undefined
        : data.gongkai === '1' || data.gongkai === 1
          ? '1'
          : '0';
    const payload = {
      uid: getUserId(),
      articleId,
      id: articleId,
      title: data.title,
      money: data.price ?? data.money,
      payContent: data.payContent || data.content,
      subTitle: data.summary || data.subTitle,
      lableName: data.tagName || data.lableName,
      fabu: data.fabu,
      matchId: data.matchId,
      team1: data.team1 || data.homeTeam,
      team2: data.team2 || data.awayTeam,
      league: data.league,
      qishu: data.period || data.qishu,
      ...data,
      articleId,
      uid: getUserId(),
      id: articleId,
      payContent: data.payContent || data.content || '',
    };
    if (data.introContent !== undefined) {
      payload.introContent = data.introContent;
    }
    if (data.disclaimer !== undefined) {
      payload.disclaimer = data.disclaimer;
    }
    if (data.drawNumbers !== undefined) {
      payload.drawNumbers = data.drawNumbers;
    }
    if (gongkai !== undefined) {
      payload.gongkai = gongkai;
    }
    const res = await zqCall('api/updateArticle', payload, { modal: true });
    return res.code === 0 ? ok(mapArticle(res.data) || res.data) : res;
  },

  // ========== 号码生成工具（本地复刻的号码组合生成器） ==========
  /** 生成器清单：按彩种分组，前端据此动态渲染表单 */
  async numberGenSpecs() {
    const res = await zqCall('api/numberGenSpecs', {});
    // 2026-10-17 修复：原写法 ok(res.data?.specs) 把数组又包成 {code,data} 对象，
    // topublish 里 ngSpecs.value = list 拿到对象而非数组 → 生成器下拉恒为「请选择」
    return res.code === 0 ? res.data?.specs || [] : [];
  },

  /** 生成号码：type + values -> sections / content / 报价 */
  async generateNumbers(data = {}) {
    const res = await zqCall('api/generateNumbers', data);
    return res.code === 0 ? ok(res.data) : res;
  },

  /** 导出报价单（返回 base64，H5 侧转 blob 下载） */
  async exportNumberQuote(data = {}) {
    const res = await zqCall('api/numberGenQuote', data, { showLoading: false });
    return res.code === 0 ? ok(res.data) : res;
  },

  async deleteArticle(id) {
    const res = await zqCall(
      'api/deleteArticle',
      { articleId: id, id, uid: getUserId() },
      { modal: true },
    );
    return res.code === 0 ? ok({ success: true, sold: res.data?.sold === true }) : res;
  },

  /**
   * 单篇下架（2026-11-03）：只带 fabu=0，不传任何内容字段。
   * 后端对已售出方案、上级下发的只读副本都只放行这种「仅下架」请求；
   * 编辑页整篇保存仍会被拒（内容锁死）。
   */
  async unpublishArticle(id) {
    const res = await zqCall(
      'api/updateArticle',
      { articleId: id, uid: getUserId(), fabu: '0' },
      { modal: true },
    );
    return res.code === 0 ? ok({ success: true }) : res;
  },

  /** 一键开奖锁定开：按中奖规则生成必中付费内容 */
  async genHitPayContent(data = {}) {
    const res = await zqCall(
      'api/genHitPayContent',
      { ...data, uid: getUserId() },
      { modal: true },
    );
    return res.code === 0 ? ok(res.data) : res;
  },

  /** 商家方案管理批量操作：本店自营方案按分类+期数 一键开奖/上架/下架/公开（2026-10-20） */
  async batchOperateArticles(data) {
    const res = await zqCall(
      'api/batchOperateArticles',
      { ...data, uid: getUserId() },
      { modal: true },
    );
    return res.code === 0 ? ok(res.data) : res;
  },

  /** 一键修改：按期数查本店自营方案（含付费内容） */
  async listArticlesByQishu(data = {}) {
    const res = await zqCall(
      'api/listArticlesByQishu',
      { ...data, uid: getUserId() },
      { showLoading: true },
    );
    if (res.code !== 0) return res;
    const list = Array.isArray(res.data?.dataList)
      ? res.data.dataList
      : Array.isArray(res.data)
        ? res.data
        : [];
    return ok(list);
  },

  /** 一键修改：批量改中/不中、置顶（已售仍可改） */
  async batchUpdateHitZhiding(data = {}) {
    const payload = { ids: data.ids || [], uid: getUserId() };
    if (Object.prototype.hasOwnProperty.call(data, 'hit')) payload.hit = data.hit;
    if (Object.prototype.hasOwnProperty.call(data, 'zhiding')) payload.zhiding = data.zhiding;
    const res = await zqCall('api/batchUpdateHitZhiding', payload, { modal: true });
    return res.code === 0 ? ok(res.data) : res;
  },

  /** 商家视角「一键同步」：把本店该期（或全部）上架方案同步到直接下级（二级代理）副本 */
  async syncMyArticlesToChildren(data = {}) {
    // qishu 非空 = 只发该期（2026-11-04，避免几千条全量比对导致几十秒）；留空 = 全量，旧行为
    const payload = { ids: data.ids || [], uid: getUserId(), qishu: data.qishu || '' };
    const res = await zqCall('api/syncMyArticlesToChildren', payload, {
      modal: true,
      showLoading: false,
      timeout: 300000,
    });
    return res.code === 0 ? ok(res.data) : res;
  },

  /** 商家视角「一键分享」：同一期口径，下级缺失新建 + 已存在对齐 */
  async shareMyArticlesToChildren(data = {}) {
    const payload = { ids: data.ids || [], uid: getUserId(), qishu: data.qishu || '' };
    const res = await zqCall('api/shareMyArticlesToChildren', payload, {
      modal: true,
      showLoading: false,
      timeout: 300000,
    });
    return res.code === 0 ? ok(res.data) : res;
  },

  /** 商家「一键同步/分享」期数下拉：本店已上架方案，最新期在前，带每期篇数 */
  async myQishuOptions() {
    const res = await zqCall('api/myQishuOptions', { uid: getUserId() }, { showLoading: false });
    return res.code === 0 ? ok(res.data) : res;
  },

  async modifyArticleSort(data) {
    const res = await zqCall('api/modifyArticleSort', data);
    return res.code === 0 ? ok(res.data) : res;
  },

  async saveOrder({ articleId }) {
    const res = await zqCall('api/saveOrder', appendPromoFields({
      id: articleId,
      uid: getUserId(),
    }), { modal: true });
    if (res.code !== 0) return res;
    return ok(mapOrder(res.data) || res.data);
  },

  async payOrder({ orderId, payType, articleId, scene, openId }) {
    let order = null;
    if (orderId) {
      const detail = await this.getOrderById(orderId);
      if (detail.code === 0) order = detail.data;
    }
    if (!order && articleId) {
      const saveRes = await this.saveOrder({ articleId });
      if (saveRes.code !== 0) return saveRes;
      order = saveRes.data;
    }
    if (!order?.id) return fail('订单不存在');

    const { resolvePayScene, getStoredOpenId } = await import('@/sheep/helper/zq-pay');
    const payTypeMap = { wechat: '3', alipay: '5', balance: '1' };
    const mappedPayType = payTypeMap[payType] || payType || '3';
    const res = await zqCall('api/payOrder', appendPromoFields({
      id: order.id,
      payType: mappedPayType,
      returl: '',
      scene: scene || resolvePayScene(payType || mappedPayType),
      openId: openId || getStoredOpenId(),
    }));
    if (res.code !== 0) return res;
    return ok(mapPayResult(res.data, payType));
  },

  async mYorderList(params = {}) {
    const res = await zqCall('api/mYorderList', {
      id: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
      title: params.merchantKeyword || params.keyword || '',
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map(mapOrder).filter(Boolean);
    return ok({ list, total: res.data.totalCount || list.length });
  },

  async orderList(params = {}) {
    const res = await zqCall('api/orderList', {
      id: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map(mapOrder).filter(Boolean);
    return ok({ list, total: res.data.totalCount || list.length });
  },

  async getOrderById(id) {
    const res = await this.mYorderList({ pageSize: 100 });
    if (res.code !== 0) return fail('订单不存在');
    const order = res.data.list.find((o) => String(o.id) === String(id));
    if (!order) return fail('订单不存在');
    const articleRes = order.articleId ? await this.getArticleById(order.articleId, { silent: true }) : null;
    const article = articleRes?.code === 0 ? articleRes.data : null;
    const merged = { ...order, article };
    if (article) {
      if (!merged.homeLogo) merged.homeLogo = article.homeLogo || '';
      if (!merged.awayLogo) merged.awayLogo = article.awayLogo || '';
      if (!merged.homeTeam) merged.homeTeam = article.homeTeam || '';
      if (!merged.awayTeam) merged.awayTeam = article.awayTeam || '';
    }
    return ok(merged);
  },

  async getHomeData() {
    // 先解析推广码商家，供首页过滤
    await this.resolvePromoMerchant();
    const [merchantRes, articleRes, msgRes, displayRes] = await Promise.all([
      zqCall('api/getMerchantList', { pageNo: 1, pageSize: 10 }, { showLoading: false }),
      fetchArticles({ pageNo: 1, pageSize: 20, isIndex: '1' }),
      zqCall('api/msgList', {}, { showLoading: false }),
      this.getDisplayConfig(),
    ]);

    const merchants = merchantRes.code === 0 ? pickList(merchantRes.data).map(mapMerchant) : [];
    const forumMerchant = merchants.find((m) => String(m.id || '').includes('agent001'))
      || merchants.find((m) => (m.remarks || m.intro || m.expertIntro || '').trim())
      || merchants[0];
    if (forumMerchant?.id) {
      uni.setStorageSync('dailishangID', forumMerchant.id);
    }

    // 不再用 forumMerchant.id 二次过滤：空店壳代理（t{tenant}_agent001）无下属专家，会把平台方案滤空
    let hotArticles = articleRes.code === 0 ? articleRes.data.list : [];
    const notices = msgRes.code === 0
      ? pickList(msgRes.data).map((m) => m.message || m.title || m.content).filter(Boolean)
      : [];

    const displayCfg = displayRes.code === 0 ? displayRes.data : {};
    const forum = mapForumFromMerchant(forumMerchant, {
      title: displayCfg.forumTitle,
      intro: displayCfg.forumIntro,
      onlineCount: displayCfg.forumOnlineCount,
    });

    return ok({
      forum,
      onlineCount: forum.onlineCount,
      banners: [{ id: 1, image: '/static/zvkf9s/new/bg.png', link: '' }],
      notices: notices.length ? notices : ['平台不会私下联系用户，请警惕诈骗'],
      hotArticles,
      hotMerchants: merchants.slice(0, 3),
      hotMatches: [],
      displayConfig: displayRes.code === 0 ? mapDisplayConfig(displayRes.data) : mapDisplayConfig({}),
    });
  },

  async getDisplayConfig(force = false) {
    if (displayConfigCache && !force) return ok(displayConfigCache);
    const res = await zqCall('api/getDisplayConfig', {}, { showLoading: false });
    if (res.code !== 0) {
      displayConfigCache = mapDisplayConfig({});
      return ok(displayConfigCache);
    }
    displayConfigCache = mapDisplayConfig(res.data);
    return ok(displayConfigCache);
  },

  async resolvePromoMerchant() {
    const invite = getShareInvite();
    if (!invite) return ok({ promoterId: getShareId() || '' });
    const res = await zqCall('api/resolveInvite', appendPromoFields({
      uid: getUserId(),
      id: getUserId(),
    }), { showLoading: false });
    if (res.code === 0 && res.data?.promoterId) {
      // 店级邀请固定 t{N}_agent001，避免 resolve 返回店主个人 id 后被错误会员店 cookie 盖掉
      const inviteShop = getInviteShopFilterId();
      const fid = inviteShop || String(res.data.promoterId);
      uni.setStorageSync('wxfid', fid);
      return ok({ ...res.data, promoterId: fid });
    }
    return res.code === 0 ? ok(res.data || {}) : res;
  },

  async getMerchantVisitorStat() {
    const res = await zqCall('api/getMerchantVisitorStat', {
      id: getUserId(),
      uid: getUserId(),
    });
    if (res.code !== 0) return res;
    const mapRow = (item) => ({
      id: item.id,
      nickname: item.nickname || '访客',
      avatar: item.icon || '',
      mobile: item.phone || '',
      visitTime: item.visitTime || 0,
    });
    const todayList = (res.data.todayList || []).map(mapRow);
    const yesterdayList = (res.data.yesterdayList || []).map(mapRow);
    const recentList = (res.data.recentList || pickList(res.data) || []).map(mapRow);
    return ok({
      visitQty: Number(res.data.visitQty || 0),
      dayVisitQty: Number(res.data.dayVisitQty ?? todayList.length),
      yesterdayVisitQty: Number(res.data.yesterdayVisitQty ?? yesterdayList.length),
      recentVisitQty: Number(res.data.recentVisitQty ?? recentList.length),
      todayList,
      yesterdayList,
      recentList,
      list: recentList,
    });
  },

  async getBalanceDetailList(params = {}) {
    const res = await zqCall('api/getBalanceDetailList', {
      id: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map(mapBalanceDetail);
    return ok({
      list,
      total: res.data.totalCount || list.length,
      dayMoney: res.data.dayMoney,
      sumMoney: res.data.sumMoney,
      balance: res.data.balance,
    });
  },

  async getTixianList(params = {}) {
    const res = await zqCall('api/getTixianList', {
      id: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map(mapWithdraw);
    return ok({
      list,
      total: res.data.totalCount || list.length,
    });
  },

  async getWithdrawFeeConfig(params = {}) {
    const res = await zqCall('api/getWithdrawFeeConfig', {
      money: params.money,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const data = res.data?.data || res.data || {};
    return ok({
      feePercent: Number(data.feePercent ?? 10),
      enabled: Number(data.enabled ?? 1),
      remark: data.remark || '',
      feeAmount: data.feeAmount != null ? Number(data.feeAmount) : undefined,
      actualAmount: data.actualAmount != null ? Number(data.actualAmount) : undefined,
    });
  },

  // 2026-10-15：上次提现用的收款银行卡，进提现页自动带出，避免每次重填
  async getLastWithdrawAccount(params = {}) {
    const res = await zqCall('api/lastWithdrawAccount', {
      id: getUserId(),
    }, { showLoading: false });
    if (res.code !== 0) return res;
    // 后端 putData 是把字段平铺进 ZqResult，这里直接取平铺结果
    const data = res.data || {};
    return ok({
      hasAccount: data.hasAccount === true,
      account: data.account || '',
      realName: data.realName || '',
    });
  },

  async tixian(params = {}) {
    const money = Number(params.money ?? params.amount);
    if (!money || money < 1) return fail('提现金额不能低于1元');
    if (!params.account) return fail('请填写收款账号');
    const res = await zqCall('api/tixian', {
      id: getUserId(),
      money,
      accountType: params.accountType ?? 1,
      account: params.account,
      realName: params.realName || '',
    }, { modal: true });
    if (res.code !== 0) return res;
    return ok({
      msg: res.msg || '提现申请已提交',
      withdrawId: res.data?.withdrawId,
      balance: Number(res.data?.balance ?? 0),
    });
  },

  // ==================== 分账方入网（汇聚 allocFunds） ====================

  /** 我的分账方档案（未入网返回 onboarded:false） */
  async altMerchantGet() {
    const res = await zqCall('api/altMerchantGet', { uid: getUserId() }, { showLoading: false });
    if (res.code !== 0) return res;
    return ok(res.data?.data || res.data || {});
  },

  /** 提交入网资料（altmch.create） */
  async altMerchantApply(data = {}) {
    const res = await zqCall('api/altMerchantApply', {
      uid: getUserId(),
      altMchName: data.altMchName,
      altMerchantType: data.altMerchantType,
      legalPerson: data.legalPerson,
      idCardNo: data.idCardNo,
      bankAccountType: data.bankAccountType,
      bankAccountNo: data.bankAccountNo,
      bankAccountName: data.bankAccountName,
      bankChannelNo: data.bankChannelNo || '',
      licenseNo: data.licenseNo || '',
      busiContactName: data.busiContactName || '',
      busiContactMobileNo: data.busiContactMobileNo || '',
      manageAddr: data.manageAddr || '',
    }, { modal: true });
    return res;
  },

  /** 上传资质图片（身份证正反面/营业执照，base64 dataURL） */
  async altMerchantUploadPics(pics = {}) {
    const res = await zqCall('api/altMerchantUploadPics', {
      uid: getUserId(),
      cardPositive: pics.cardPositive || '',
      cardNegative: pics.cardNegative || '',
      tradeLicence: pics.tradeLicence || '',
    }, { modal: true });
    return res;
  },

  /** 获取签约页地址 */
  async altMerchantSignUrl(successForwardUrl = '') {
    const res = await zqCall('api/altMerchantSignUrl', {
      uid: getUserId(),
      successForwardUrl,
    }, { showLoading: true });
    if (res.code !== 0) return res;
    return ok(res.data?.data || res.data || {});
  },

  /** 主动同步汇聚状态（认证/图片/签约三查） */
  async altMerchantRefresh() {
    const res = await zqCall('api/altMerchantRefresh', { uid: getUserId() }, { showLoading: true });
    if (res.code !== 0) return res;
    return ok(res.data?.data || res.data || {});
  },

  /** 分账方 333 账户余额 */
  async altMerchantAccount() {
    const res = await zqCall('api/altMerchantAccount', { uid: getUserId() }, { showLoading: false });
    if (res.code !== 0) return res;
    return ok(res.data?.data || res.data || {});
  },

  async bindWx(params = {}) {
    const code = params.code;
    if (!code) return fail('授权失败，请重新授权');
    const silent = !!params.silent;
    const res = await zqCall(
      'api/bindWx',
      { id: getUserId(), code },
      { modal: !silent, showLoading: !silent },
    );
    if (res.code !== 0) return res;
    setZqSession(res.data);
    return ok({ member: mapMember(res.data) });
  },

  /** 后台商家扫码：ticket + 微信 code → 绑定 openId 到结算会员 */
  async bindWxByTicket(params = {}) {
    const ticket = params.ticket;
    const code = params.code;
    if (!ticket) return fail('二维码已失效，请回后台重新生成');
    if (!code) return fail('授权失败，请重新扫码');
    const res = await zqCall(
      'api/bindWxByTicket',
      { ticket, code },
      { modal: true, showLoading: true },
    );
    if (res.code !== 0) return res;
    return ok({
      msg: res.msg || '绑定成功',
      merchantId: res.data?.merchantId,
      wxBound: true,
    });
  },

  async unbindWx() {
    const res = await zqCall('api/unbindWx', { id: getUserId() }, { modal: true });
    return res.code === 0 ? ok({ msg: '解绑成功' }) : res;
  },

  async getRechargeTierList() {
    const res = await zqCall('api/getRechargeTierList', {}, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data)
      .map(mapRechargeTier)
      .sort((a, b) => a.sort - b.sort);
    return ok({ list });
  },

  async recharge({ tierId, amount, payType, scene, openId }) {
    const { resolvePayScene, getStoredOpenId } = await import('@/sheep/helper/zq-pay');
    const body = { id: getUserId() };
    if (tierId != null && tierId !== '') {
      body.tierId = tierId;
    } else {
      const money = Number(amount);
      if (!money || money <= 0) return fail('请选择充值面值');
      body.money = money;
    }
    if (payType) body.payType = payType;
    body.scene = scene || resolvePayScene(payType);
    body.openId = openId || getStoredOpenId();
    const res = await zqCall('api/recharge', body, { modal: true });
    if (res.code !== 0) return res;
    return ok(mapPayResult({
      ...res.data,
      pending: !!(res.data?.pending || res.data?.payUrl || res.data?.jsapiParams || res.data?.alipaySchemeUrl),
      balance: Number(res.data?.balance ?? 0),
    }, payType));
  },

  async getMemberShareList(params = {}) {
    const res = await zqCall('api/getMemberShareList', {
      id: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map(mapChild);
    return ok({ list, total: res.data.totalCount || list.length });
  },

  async getMemberShareOnLineList(params = {}) {
    const res = await zqCall('api/getMemberShareOnLineList', {
      id: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map(mapChild);
    return ok({ list, total: res.data.totalCount || list.length });
  },

  async getMyChildrenList(params = {}) {
    const res = await zqCall('api/getMyChildrenList', {
      id: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map(mapChild);
    return ok({ list, total: res.data.totalCount || list.length });
  },

  async getMySubList(params = {}) {
    const res = await zqCall('api/getMySubList', {
      id: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map(mapChild);
    return ok({ list, total: res.data.totalCount || list.length });
  },

  async getLevelOrders(params = {}) {
    const res = await zqCall('api/orderList2', {
      id: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
    }, { showLoading: false });
    if (res.code !== 0) return this.orderList(params);
    const list = pickList(res.data).map(mapOrder);
    return ok({ list, total: res.data.totalCount || list.length });
  },

  async shareUrl() {
    const res = await zqCall('api/shareUrl', { id: getUserId() }, { showLoading: false });
    if (res.code !== 0) return res;
    return ok({ url: res.data.url || res.data.shareUrl || res.data });
  },

  async shareQrcode() {
    const res = await zqCall('api/shareQrcode', { id: getUserId() }, { showLoading: true });
    if (res.code !== 0) return res;
    return ok({
      url: res.data.url || res.data.shareUrl,
      qrcode: res.data.qrcode,
    });
  },

  async setFencheng({ rate }) {
    const res = await zqCall('api/setFencheng', { id: getUserId(), percent: rate }, { modal: true });
    return res.code === 0 ? ok({ msg: '分成设置成功', rate }) : res;
  },

  async getDailishangID() {
    const res = await zqCall('api/getDailishangID', {}, { showLoading: false });
    if (res.code === 0 && res.data?.id) uni.setStorageSync('dailishangID', res.data.id);
    return res;
  },

  async getAgreementList() {
    const res = await zqCall('api/getAgreementList', {});
    if (res.code !== 0) return res;
    return ok(pickList(res.data).map(mapAgreement));
  },

  async getAgreement(type = 'privacy') {
    const res = await this.getAgreementList();
    if (res.code !== 0) return res;
    const list = res.data || [];
    const match =
      list.find((item) => item.type === type) ||
      list.find((item) => item.title?.includes(type === 'privacy' ? '隐私' : '协议')) ||
      list[0];
    if (!match) return fail('协议内容不存在');
    return ok(match);
  },

  async aboutUs() {
    const res = await zqCall('api/aboutUs', {});
    if (res.code !== 0) return res;
    return ok(mapAbout(res.data));
  },

  async getVersion() {
    const res = await zqCall('api/getVersion', {}, { showLoading: false });
    if (res.code !== 0) return ok({ version: '21', forceUpdate: false, downloadUrl: '' });
    return ok({
      version: res.data.version || res.data.versionCode,
      forceUpdate: res.data.forceUpdate === '1',
      downloadUrl: res.data.downloadUrl || res.data.url || '',
    });
  },

  async kefu() {
    const res = await zqCall('api/kefu', {});
    if (res.code !== 0) return res;
    return ok({
      image: res.data.image,
      qq: res.data.qq,
      wechat: res.data.weixin || res.data.wechat,
      phone: res.data.phone || res.data.telephone,
    });
  },

  async submitFeedback(data = {}) {
    return zqCall('api/submitFeedback', {
      id: getUserId(),
      uid: getUserId(),
      type: data.type || '',
      content: data.content || '',
      phone: data.phone || '',
      images: Array.isArray(data.images) ? data.images.filter(Boolean).join(',') : (data.images || ''),
    });
  },

  async feedbackList(params = {}) {
    const res = await zqCall('api/feedbackList', {
      id: getUserId(),
      uid: getUserId(),
      pageNo: params.pageNo || 1,
      pageSize: params.pageSize || 50,
    }, { showLoading: false });
    if (res.code !== 0) return res;
    const list = pickList(res.data).map((item) => ({
      id: item.id,
      type: item.type || '',
      content: item.content || '',
      phone: item.phone || '',
      images: String(item.images || '').split(',').filter(Boolean),
      status: Number(item.status || 0),
      statusText: item.statusText || (Number(item.status) === 1 ? '已回复' : '待处理'),
      reply: item.reply || '',
      replyTime: item.replyTime || '',
      createTime: item.createTime || item.addTime || '',
    }));
    return ok({ list, total: res.data.totalCount || list.length });
  },

  async msgList() {
    const res = await zqCall('api/msgList', {}, { showLoading: false });
    if (res.code !== 0) return ok({ list: [] });
    const list = pickList(res.data).map((item, idx) => ({
      id: item.id || idx + 1,
      title: item.title || '系统通知',
      content: item.message || item.content || '',
      time: item.addTime || item.createTime || '',
      read: item.read === '1',
    }));
    return ok({ list });
  },

  async notifyList(params = {}) {
    const uid = getUserId();
    const res = await zqCall(
      'api/notifyList',
      { id: uid, pageSize: params.pageSize || 50 },
      { showLoading: false },
    );
    if (res.code !== 0) return ok({ list: [] });
    const list = pickList(res.data).map((item) => ({
      id: item.id,
      source: item.source || (String(item.id || '').startsWith('a-') ? 'announce' : 'notify'),
      type: item.type || '',
      title: item.title || '通知',
      content: item.content || item.message || '',
      bizType: item.bizType || '',
      bizId: item.bizId || '',
      read: item.read === true || item.read === 1 || item.read === '1',
      time: item.createTime || item.time || '',
    }));
    return ok({ list });
  },

  async notifyUnreadCount() {
    const uid = getUserId();
    if (!uid) return ok({ count: 0 });
    const res = await zqCall('api/notifyUnreadCount', { id: uid }, { showLoading: false });
    if (res.code !== 0) return ok({ count: 0 });
    const count = Number(res.data?.count ?? res.data?.unreadCount ?? 0) || 0;
    return ok({ count });
  },

  async notifyRead(notifyId, readAll = false) {
    const uid = getUserId();
    if (!uid) return fail('请先登录');
    const body = { id: uid };
    if (readAll) body.readAll = true;
    else body.notifyId = notifyId;
    return zqCall('api/notifyRead', body, { showLoading: false });
  },

  async getWxConfig(url) {
    return zqCall('api/getWxConfig', { url }, { showLoading: false });
  },

  async getYumingList() {
    return zqCall('api/getYumingList', {}, { showLoading: false });
  },

  async autoRegist(params = {}) {
    return zqCall('api/autoRegist', appendPromoFields({ ...params }));
  },

  isLogin() {
    return isLoggedIn || syncLoginState();
  },

  logout() {
    isLoggedIn = false;
    currentUser = null;
    logoutLocal();
  },
};

export default ZqApi;
